import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Button, Input, Modal, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useApolloClient } from 'react-apollo';
import GET_NEXXEN_SEGMENTS from '../../../core/GraphQl/Queries/GET_NEXXEN_SEGMENTS';

const { Text } = Typography;

const CampaignRequestorNexxenSegmentsDialog = ({
  visible,
  setVisible,
  onAddSelections,
  selectedSegments = [],
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [segmentProgress, setSegmentProgress] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const client = useApolloClient();

  // Ref to track if component is mounted (prevent memory leaks)
  const isMountedRef = useRef(true);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleAddSelections = () => {
    const selectedItems = segments
      .filter((item) => selectedRowKeys.includes(item.id))
      .map((item) => ({
        segmentId: item.id,
        name: item.name,
        segmentType: 'segment',
        cost: item.cpmCost || 0,
        dataProvider: item.dataProvider,
      }));

    onAddSelections(selectedItems);
    setVisible(false);
  };

  const rowSelection = {
    type: 'radio',
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // Fetch all segments using batched API calls (100 at a time)
  const fetchSegments = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoadingSegments(true);
    setSegmentProgress('Loading segments...');
    let allSegments = [];
    let offset = 0;
    const limit = 100;

    try {
      while (isMountedRef.current) {
        const { data } = await client.query({
          query: GET_NEXXEN_SEGMENTS,
          variables: {
            marketId: '2184',
            segmentUsage: 'targeting',
            offset,
            limit,
          },
          fetchPolicy: 'network-only',
        });

        if (!isMountedRef.current) return;

        const pageData = data?.nexxenSegments?.data || [];
        const total = data?.nexxenSegments?.queryTotal || 0;

        allSegments = [...allSegments, ...pageData];
        setSegmentProgress(`Loaded ${allSegments.length} of ${total} segments...`);

        if (allSegments.length >= total || pageData.length === 0) break;
        offset += limit;
      }

      if (isMountedRef.current) {
        setSegments(allSegments);
      }
    } catch (error) {
      console.error('Error fetching segments:', error);
      if (isMountedRef.current) {
        message.error('Failed to load segments');
        setSegments([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingSegments(false);
        setSegmentProgress('');
      }
    }
  }, [client]);

  // Set mounted ref on mount, clear on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load data when modal opens (only if not already loaded)
  useEffect(() => {
    if (visible && segments.length === 0) {
      fetchSegments();
    }
  }, [visible, segments.length, fetchSegments]);

  // Initialize selected row keys from props
  useEffect(() => {
    if (selectedSegments && selectedSegments.length > 0) {
      const keys = selectedSegments.map((s) => s.segmentId);
      setSelectedRowKeys(keys);
    } else {
      setSelectedRowKeys([]);
    }
  }, [selectedSegments, visible]);

  // Client-side filtering
  const filteredSegments = useMemo(() => {
    if (!searchTerm) return segments;
    const lowerSearch = searchTerm.toLowerCase();
    return segments.filter((item) =>
      item.id?.toString().includes(searchTerm) ||
      item.name?.toLowerCase().includes(lowerSearch) ||
      item.description?.toLowerCase().includes(lowerSearch) ||
      item.dataProvider?.toLowerCase().includes(lowerSearch)
    );
  }, [segments, searchTerm]);

  // Client-side pagination
  const paginatedSegments = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSegments.slice(start, start + pageSize);
  }, [filteredSegments, currentPage, pageSize]);

  const segmentColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Data Provider',
      dataIndex: 'dataProvider',
      key: 'dataProvider',
      width: 150,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 180,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: 'CPM Cost',
      dataIndex: 'cpmCost',
      key: 'cpmCost',
      width: 100,
      render: (value) => (value ? `$${value.toFixed(2)}` : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status || 'Unknown'}
        </Tag>
      ),
    },
  ];

  const handlePaginationChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
    } else {
      setCurrentPage(page);
    }
  };

  // Reset pagination when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <Modal
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: 20,
          }}
        >
          <span>NEXXEN SEGMENTS</span>
          <div>
            <Button
              type="primary"
              disabled={selectedRowKeys.length === 0}
              onClick={() => handleAddSelections()}
            >
              Add Selection
            </Button>
          </div>
        </div>
      }
      visible={visible}
      onCancel={() => setVisible(false)}
      footer={null}
      width={900}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Text type="secondary">
          Select a segment to add to this line item for audience targeting.
          Only one segment can be attached per line item.
        </Text>

        <Input
          placeholder="Search by segment ID, name, description, or data provider..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
        />

        {loadingSegments ? (
          <Space direction="vertical" style={{ width: '100%', padding: 40 }} align="center">
            <Spin />
            <Text type="secondary">{segmentProgress || 'Loading segments...'}</Text>
          </Space>
        ) : (
          <Table
            rowKey={(record) => record.id}
            rowSelection={rowSelection}
            columns={segmentColumns}
            dataSource={paginatedSegments}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: filteredSegments.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              onChange: handlePaginationChange,
            }}
            scroll={{ y: 400 }}
            size="small"
          />
        )}
      </Space>
    </Modal>
  );
};

export default CampaignRequestorNexxenSegmentsDialog;
